import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import type { Campaign } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/toast/ToastProvider';
import pageStyles from '@/pages/ProjectsPage.module.css';

type BrevoStats = {
  sent?: number;
  delivered?: number;
  uniqueViews?: number;
  clickers?: number;
  hardBounces?: number;
  softBounces?: number;
  unsubscribed?: number;
};

type BrevoSendRow = {
  brevo_campaign_id: number;
  brevo_status: string | null;
  stats: BrevoStats;
  last_stats_at: string | null;
  sent_at: string | null;
  variant_key: string | null;
  email_template_id: string | null;
  email_template_version: number | null;
};

type Props = {
  campaign: Campaign;
  hasBrevoIntegration: boolean;
};

export function CampaignBrevoPanel({ campaign, hasBrevoIntegration }: Props) {
  const qc = useQueryClient();
  const toast = useToast();

  const brevoQ = useQuery({
    queryKey: ['campaign-brevo', campaign.id],
    enabled: Boolean(campaign.id),
    queryFn: () =>
      apiFetch<{ brevo: BrevoSendRow | null; sends: BrevoSendRow[] }>(
        `/api/campaigns/${campaign.id}/brevo`,
      ),
  });

  const sendBroadcast = useMutation({
    mutationFn: () =>
      apiFetch<{ contact_count: number; sends: { brevo_campaign_id: number; variant_key: string | null }[] }>(
        `/api/campaigns/${campaign.id}/brevo/broadcast`,
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (data) => {
      const n = data.sends?.length ?? 1;
      toast.push(
        `Brevo broadcast — ${data.contact_count} contacten, ${n} Brevo-campagne(s)`,
        'success',
      );
      void qc.invalidateQueries({ queryKey: ['campaign-brevo', campaign.id] });
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const refreshStats = useMutation({
    mutationFn: () =>
      apiFetch<{ stats: BrevoStats }>(`/api/campaigns/${campaign.id}/brevo/stats/refresh`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => {
      toast.push('Brevo-statistieken bijgewerkt', 'success');
      void qc.invalidateQueries({ queryKey: ['campaign-brevo', campaign.id] });
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  if (campaign.type !== 'broadcast') return null;
  if (!hasBrevoIntegration) {
    return (
      <section className={pageStyles.card}>
        <p className={pageStyles.mutedSmall}>Broadcast via Brevo vereist een actieve Brevo-integratie.</p>
      </section>
    );
  }

  const sends = brevoQ.data?.sends ?? (brevoQ.data?.brevo ? [brevoQ.data.brevo] : []);

  return (
    <section className={pageStyles.card}>
      <h3>Brevo marketing broadcast</h3>
      <p className={pageStyles.mutedSmall}>
        Gebruikt <strong>stap 1</strong> van de sequence, inclusief gekoppelde{' '}
        <strong>e-mailtemplate</strong> (HTML/blokken + plain text). Bij een A/B-experiment met ≥2
        varianten: aparte Brevo-campagnes per variant met sticky verdeling. Hub-merge-tags worden
        omgezet naar Brevo-contactvelden (FIRSTNAME, COMPANY).
      </p>
      {!sends.length && ['draft', 'scheduled', 'paused'].includes(campaign.status) ? (
        <Button variant="primary" disabled={sendBroadcast.isPending} onClick={() => sendBroadcast.mutate()}>
          {sendBroadcast.isPending ? 'Versturen…' : 'Verstuur via Brevo'}
        </Button>
      ) : null}
      {sends.length ? (
        <>
          {sends.map((row) => {
            const stats = row.stats ?? {};
            return (
              <div key={row.brevo_campaign_id} style={{ marginTop: '1rem' }}>
                <p>
                  Brevo #{row.brevo_campaign_id}
                  {row.variant_key ? ` · variant ${row.variant_key}` : ''}
                  {row.email_template_id
                    ? ` · template v${row.email_template_version ?? '?'}`
                    : ''}
                  {row.brevo_status ? ` — ${row.brevo_status}` : ''}
                </p>
                <ul className={pageStyles.list}>
                  <li>Verzonden: {stats.sent ?? '—'}</li>
                  <li>Geleverd: {stats.delivered ?? '—'}</li>
                  <li>Clicks: {stats.clickers ?? '—'}</li>
                  <li>Bounces: {(stats.hardBounces ?? 0) + (stats.softBounces ?? 0) || '—'}</li>
                  <li>Uitschrijvingen: {stats.unsubscribed ?? '—'}</li>
                </ul>
              </div>
            );
          })}
          <Button variant="secondary" disabled={refreshStats.isPending} onClick={() => refreshStats.mutate()}>
            Statistieken verversen
          </Button>
        </>
      ) : null}
    </section>
  );
}
